import type { Ref } from 'react';
import { FileText, Folder, Layers } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import type { WorkspaceRow } from './workspace';
import { rowKey } from './workspace-dnd-rules';

export function WorkspaceDragPreview({
  rows,
  previewRef,
  transform
}: {
  rows: WorkspaceRow[];
  previewRef: Ref<HTMLDivElement>;
  transform: string;
}) {
  const { t } = useTranslation();
  const { getColour, getBrandTint, getContrastTextColor, getShadow } =
    useThemeContext();
  const accent = getColour('activeText');
  const label = t('workspace-drag-items', { count: rows.length });
  const remaining = rows.length - 3;
  return (
    <div
      ref={previewRef}
      data-workspace-drag-preview
      role="status"
      aria-label={label}
      className="fixed left-0 top-0 z-[10000] pointer-events-none select-none w-[280px] max-w-[calc(100vw-24px)]"
      style={{ transform, color: getColour('normalText') }}
    >
      {rows.length > 1 &&
        [2, 1].map(depth => (
          <div
            key={depth}
            aria-hidden="true"
            className="absolute inset-0 rounded-2xl border"
            style={{
              transform: `translateY(${depth * 7}px) scale(${1 - depth * 0.035})`,
              backgroundColor: getColour('secondLayer'),
              borderColor: getBrandTint(0.35)
            }}
          />
        ))}
      <div
        className="relative overflow-hidden rounded-2xl border"
        style={{
          backgroundColor: getColour('thirdLayer'),
          borderColor: getBrandTint(0.45),
          boxShadow: getShadow('pop')
        }}
      >
        <div
          className="flex items-center gap-2.5 px-4 py-3 border-b"
          style={{
            backgroundColor: getBrandTint(0.1),
            borderColor: getColour('hairline')
          }}
        >
          <Layers size={16} aria-hidden="true" style={{ color: accent }} />
          <span className="flex-1 text-xs font-semibold">{label}</span>
          <span
            className="flex h-7 min-w-7 items-center justify-center rounded-full px-2 text-xs font-bold tabular-nums"
            style={{
              backgroundColor: accent,
              color: getContrastTextColor(accent)
            }}
          >
            {rows.length}
          </span>
        </div>
        <ul className="px-3 py-2">
          {rows.slice(0, 3).map(row => {
            const Icon = row.variant === 'folder' ? Folder : FileText;
            return (
              <li key={rowKey(row)} className="flex items-center gap-3 py-1.5">
                <span
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                  style={{ backgroundColor: getBrandTint(0.1), color: accent }}
                >
                  <Icon size={17} aria-hidden="true" />
                </span>
                <span className="truncate text-sm font-medium">{row.name}</span>
              </li>
            );
          })}
        </ul>
        {remaining > 0 && (
          <div
            className="border-t px-4 py-2.5 text-xs font-medium"
            style={{
              borderColor: getColour('hairline'),
              color: getColour('inactiveText')
            }}
          >
            {t('workspace-drag-more', { count: remaining })}
          </div>
        )}
      </div>
    </div>
  );
}
